/**
 * Gemeinsame Bausteine für Fehlerhinweise externer Registerabfragen (MaStR, ZEREZ).
 *
 * <p>Das Backend liefert im Fehlerfall eine RFC-7807-Problemantwort `{ status, title, detail }`,
 * deren `detail` mit einem stabilen Maschinen-Code beginnt (z. B. `MASTR_NOT_FOUND`,
 * `ZEREZ_TIMEOUT`). Code (ersatzweise der HTTP-Status) bestimmt die Fehlerkategorie; die
 * registerspezifischen Adapter (`mastr-error-hint.ts`, `zerez-hint.ts`) ordnen der Kategorie Titel
 * und Meldung zu, der rollenabhängige Handlungshinweis kommt von hier:
 *
 * <ul>
 *   <li><b>Konfigurationsfehler</b> (fehlende/ungültige Anbindung): Sachbearbeiter sollen ein
 *       Ticket für das Administrator-Team der Netzbetreiberplattform erstellen, Installateure sich
 *       an ihren Sachbearbeiter wenden.
 *   <li><b>Server-/Verbindungsfehler</b> (500/502/504): später erneut versuchen; bei anhaltendem
 *       Problem dieselben Kontakte wie bei Konfigurationsfehlern.
 *   <li><b>Nummer nicht gefunden / ungültige Eingabe</b>: reiner Nutzerhinweis ohne Kontakt-Hinweis.
 * </ul>
 */

export interface RegisterErrorHint {
  title: string;
  message: string;
  /** Rollenabhängiger Handlungshinweis (Ticket bzw. Sachbearbeiter). Fehlt bei reinen Nutzerfehlern. */
  hint?: string;
}

export type RegisterErrorCategory = 'notFound' | 'invalidInput' | 'configuration' | 'transient' | 'unknown';

/**
 * Extrahiert den führenden Maschinen-Code aus dem `detail`-Feld der Problemantwort
 * (z. B. `"MASTR_NOT_FOUND: ..."` → `"MASTR_NOT_FOUND"`). Liefert `null`, wenn keiner der bekannten
 * Codes vorangestellt ist.
 */
export function errorCodeFromDetail<C extends string>(
  detail: string | null | undefined,
  knownCodes: readonly C[]
): C | null {
  if (!detail) {
    return null;
  }
  const token = detail.trim().split(/[:\s]/)[0];
  return (knownCodes as readonly string[]).includes(token) ? (token as C) : null;
}

/** Fehlerkategorie: die des Codes, falls bekannt, sonst aus dem HTTP-Status abgeleitet. */
export function categorizeRegisterError(
  codeCategory: RegisterErrorCategory | null | undefined,
  status: number | null | undefined
): RegisterErrorCategory {
  if (codeCategory) {
    return codeCategory;
  }
  switch (status) {
    case 404:
      return 'notFound';
    case 400:
      return 'invalidInput';
    case 501:
      return 'configuration';
    case 500:
    case 502:
    case 504:
      return 'transient';
    default:
      return 'unknown';
  }
}

/** Rollenabhängiger Kontakt-Hinweis für Konfigurationsfehler. */
export function contactHint(isInstaller: boolean): string {
  return isInstaller
    ? 'Bitte wenden Sie sich an Ihren zuständigen Sachbearbeiter.'
    : 'Bitte erstellen Sie ein Ticket für das Administrator-Team der Netzbetreiberplattform.';
}

/** Rollenabhängiger Kontakt-Hinweis für anhaltende Server-/Verbindungsfehler. */
export function persistHint(isInstaller: boolean): string {
  return isInstaller
    ? 'Sollte das Problem weiterhin bestehen, wenden Sie sich bitte an Ihren zuständigen Sachbearbeiter.'
    : 'Sollte das Problem weiterhin bestehen, erstellen Sie bitte ein Ticket für das Administrator-Team der Netzbetreiberplattform.';
}
